import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { featureFlags } from "../config/featureFlags";
import { fetchWorkflowList } from "../services/complianceWorkflowApi";
import { normalizeComplianceStandardCode } from "../utils/complianceStandard";

export type WorkflowStageByStandard = Map<string, string>;

type ComplianceWorkflowIndexContextValue = {
  workflowStageByStandard: WorkflowStageByStandard;
  loading: boolean;
  refresh: () => void;
};

const ComplianceWorkflowIndexContext = createContext<ComplianceWorkflowIndexContextValue | null>(null);

export function ComplianceWorkflowIndexProvider({ children }: { children: React.ReactNode }) {
  const [workflowStageByStandard, setWorkflowStageByStandard] = useState<WorkflowStageByStandard>(new Map());
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const refresh = React.useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!featureFlags.COMPLIANCE_WORKFLOW_V1) {
      setWorkflowStageByStandard(new Map());
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void fetchWorkflowList()
      .then((data) => {
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const row of data.workflows || []) {
          const code = normalizeComplianceStandardCode(row.standardCode);
          if (code) map.set(code, row.workflowStage || "NOT_ACTIVATED");
        }
        setWorkflowStageByStandard(map);
      })
      .catch(() => {
        if (!cancelled) setWorkflowStageByStandard(new Map());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const value = useMemo(
    () => ({ workflowStageByStandard, loading, refresh }),
    [workflowStageByStandard, loading, refresh],
  );

  return <ComplianceWorkflowIndexContext.Provider value={value}>{children}</ComplianceWorkflowIndexContext.Provider>;
}

export function useComplianceWorkflowIndex(): ComplianceWorkflowIndexContextValue {
  const ctx = useContext(ComplianceWorkflowIndexContext);
  if (!ctx) {
    return {
      workflowStageByStandard: new Map(),
      loading: false,
      refresh: () => {},
    };
  }
  return ctx;
}

export function isWorkflowActiveStage(stage: string | undefined): boolean {
  return Boolean(stage && stage !== "NOT_ACTIVATED");
}
